import { useEffect, useState } from "react";
import { fetchDashboardSummary } from "../../../services/aro/reportService";
import { DashboardEventStat, DashboardSummary } from "../../../types/aro";
import StatsCard      from "../shared/StatsCard/StatsCard";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import tableauLogo    from "../../../assets/tableau_logo.svg";
import tableauPreview from "../../../assets/ARO_tableau.svg";
import "./DashboardPage.css";

export default function DashboardPage() {
  const [dashboard,      setDashboard]      = useState<DashboardSummary | null>(null);
  const [selectedEvent,  setSelectedEvent]  = useState<DashboardEventStat | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardSummary()
      .then((d) => {
        setDashboard(d);
        if (d.certs_by_event.length > 0) setSelectedEvent(d.certs_by_event[0]);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="aro-dash">
      <PageHeader title="AI Analytics" subtitle="AI Certificate Job Outcome Tracker" />

      {error && <div className="aro-error-banner">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : dashboard ? (
        <>
          {/* ── Stat cards ──────────────────────────────────── */}
          <div className="aro-dash__stats-grid">
            <StatsCard
              title="Total Certifications"
              value={(dashboard.total_certificates ?? 0).toLocaleString()}
              color="maroon"
            />
            <StatsCard
              title="Total Number of People Certified"
              value={(dashboard.total_persons ?? 0).toLocaleString()}
              color="blue"
            />
            <StatsCard
              title="Total Events"
              value={(dashboard.total_events ?? 0).toLocaleString()}
              color="amber"
            />
          </div>

          {/* ── Post-Certification Job Tracking ─────────────── */}
          <h2 className="aro-section-title" style={{ marginBottom: "var(--space-3)" }}>Post-Certification Job Tracking</h2>
          <div className="aro-tracking-card" style={{ marginBottom: "var(--space-6)" }}>
            <div className="aro-tracking-card__stat">
              <span className="aro-tracking-card__value aro-tracking-card__value--blue">
                {(dashboard.actively_tracking ?? 0).toLocaleString()}
              </span>
              <span className="aro-tracking-card__label">Actively Tracking</span>
            </div>
            <div className="aro-tracking-card__divider" />
            <div className="aro-tracking-card__stat">
              <span className="aro-tracking-card__value aro-tracking-card__value--amber">
                {(dashboard.need_attention ?? 0).toLocaleString()}
              </span>
              <span className="aro-tracking-card__label">Untrackable</span>
            </div>
            <div className="aro-tracking-card__divider" />
            <div className="aro-tracking-card__stat">
              <span className="aro-tracking-card__value aro-tracking-card__value--green">
                {(dashboard.total_job_changed ?? 0).toLocaleString()}
              </span>
              <span className="aro-tracking-card__label">Job Change Detected</span>
            </div>
          </div>

          {/* ── Tracking Methodology ── */}
          <h2 className="aro-section-title">How Candidate Tracking Works</h2>
          <div className="aro-card aro-dash__info-card">
            <ul className="aro-dash__info-list">
              <li>Candidates are tracked for 3 years after entering the system.</li>
              <li>Candidate status is checked every 3 months.</li>
              <li>A valid LinkedIn profile is required for tracking. </li>
            </ul>
          </div>

          {/* ── Certificates by event ────────────────────────── */}
          {dashboard.certs_by_event.length > 0 && selectedEvent && (() => {
            const isExternal = selectedEvent.event_id === null;

            return (
              <>
                <h2 className="aro-section-title" style={{ marginBottom: "var(--space-3)" }}>Certificates by Event</h2>
                <div className={`aro-event-card${isExternal ? " aro-event-card--external" : ""}`}>
                <select
                  className="aro-select aro-event-card__select"
                  value={selectedEvent.event_id ?? "external"}
                  onChange={(e) => {
                    const val = e.target.value;
                    const found = dashboard.certs_by_event.find(
                      (ev) => (ev.event_id === null ? "external" : String(ev.event_id)) === val
                    );
                    if (found) setSelectedEvent(found);
                  }}
                >
                  {dashboard.certs_by_event.map((ev) => (
                    <option key={ev.event_id ?? "external"} value={ev.event_id ?? "external"}>
                      {ev.event_name}
                    </option>
                  ))}
                </select>
                <div className="aro-event-card__stats">
                  <div className="aro-event-card__stat">
                    <span className="aro-event-card__stat-value">
                      {selectedEvent.total_certificates.toLocaleString()}
                    </span>
                    <span className="aro-event-card__stat-label">Certificates</span>
                  </div>
                  <div className="aro-event-card__divider" />
                  <div className="aro-event-card__stat">
                    <span className="aro-event-card__stat-value">
                      {selectedEvent.total_persons.toLocaleString()}
                    </span>
                    <span className="aro-event-card__stat-label">People</span>
                  </div>
                </div>
              </div>
              </>
            );
          })()}

          {/* ── Tableau ──────────────────────────────────────── */}
          <div className="aro-dash__tableau-heading">
            <img src={tableauLogo} alt="Tableau" width="28" height="28" />
            <h2 className="aro-section-title" style={{ margin: 0 }}>Tableau Dashboards</h2>
          </div>
          <a
            href="https://tableau.jobsohio.app/#/views/AICertificationWorkforceOutcomeTrackerDashboard/AICertificationWorkforceOutcomeTracker?:iid=1"
            target="_blank"
            rel="noopener noreferrer"
            className="aro-dash__tableau-card"
          >
            <img
              src={tableauPreview}
              alt="AI Ready Ohio Dashboard preview"
              className="aro-dash__tableau-preview"
            />
            <div className="aro-dash__tableau-footer">
              For full detailed AI Ready Ohio Analytics,{" "}
              <span className="aro-dash__tableau-link">Click Here.</span>
            </div>
          </a>
        </>
      ) : null}
    </div>
  );
}
